import { Add, Check, Close } from "@mui/icons-material";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRevalidator } from "react-router-dom";

import { useDebouncedEffect } from "../../../../../../../../hooks/useDebounceEffect";
import { useHistoryNavigate } from "../../../../../../../../hooks/useHistoryNavigate";
import useRevalidateParent from "../../../../../../../../hooks/useRevalidateParent";
import useService from "../../../../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../../../../global/ApiRequestErrorHandler";
import { truncate } from "../../../../../../../global/helpers";
import LoadingIndicator from "../../../../../../../global/LoadingIndicator";
import ResponseDisplay from "../../components/ResponseDisplay";

import {
  areCaseListsEqual,
  dedupeByCaseKey,
  getOptionKey,
  getOptionLabel,
  normalizeCaseCollection,
  splitCaseCollection,
} from "./utils";

export default function InvolvedIndividuals({ caseObj, editPermissions, viewPermissions }) {
  const revalidateParent = useRevalidateParent("rvClientDetail");
  const caseRepository = useService("repositories/case-repository");
  const [editInvolved, setEditInvolved] = useState(false);
  const [relatedCases, setRelatedCasesState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [caseSearchQuery, setCaseSearchQuery] = useState("");
  const [deBouncedCaseSearchQuery, setDeBouncedCaseSearchQuery] = useState("");
  const [responseSuccess, setResponseSuccess] = useState(null);
  const [responseBreakdown, setResponseBreakdown] = useState("");
  const [navigateToRelatedCase, setNavigateToRelatedCase] = useState(false);
  const [caseList, setCaseList] = useState([]);
  const { historyNavigate } = useHistoryNavigate();
  const { revalidate } = useRevalidator();

  const handleApiError = useCallback(
    (error) => {
      setLoading(false);
      const res = error?.response?.data ?? error;
      const errArr = ApiRequestErrorHandler(res);
      setResponseBreakdown(errArr);
      setResponseSuccess(false);
      setTimeout(() => {
        setResponseSuccess(null);
        setResponseBreakdown("");
      }, 60000);
      return error;
    },
    [setLoading, setResponseBreakdown, setResponseSuccess]
  );

  const handleApiSuccess = useCallback(
    (response, valueName) => {
      setLoading(false);
      setResponseBreakdown(`${valueName} was successfully updated`);
      setResponseSuccess(true);
      setEditInvolved(false);
      setCaseSearchQuery("");
      setTimeout(() => {
        setResponseSuccess(null);
        setResponseBreakdown("");
      }, 5000);
      revalidate();
      return response;
    },
    [
      revalidate,
      setCaseSearchQuery,
      setEditInvolved,
      setLoading,
      setResponseBreakdown,
      setResponseSuccess,
    ]
  );

  const addCaseRelationship = async () => {
    setLoading(true);

    const ids = (relatedCases || []).map((c) => c?.pk ?? c?.id).filter((v) => v !== null);

    const caseId = caseObj?.pk ?? caseObj?.id;

    try {
      const handlepatch = await caseRepository.patch(caseId, {
        related_cases: ids,
      });
      if (handlepatch) {
        handleApiSuccess(handlepatch, "Companions");
        revalidateParent();
      } else {
        throw new Error("Empty response from server.");
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const { locked: lockedRelatedCases, editable: editableBaseRelatedCases } = useMemo(
    () => splitCaseCollection(caseObj?.relatedCases ?? []),
    [caseObj]
  );
  const combinedViewCases = useMemo(
    () => normalizeCaseCollection([...lockedRelatedCases, ...relatedCases]),
    [lockedRelatedCases, relatedCases]
  );

  const setRelatedCases = useCallback(
    (list = []) => setRelatedCasesState(splitCaseCollection(list).editable),
    [setRelatedCasesState]
  );

  const handleCancelEdit = useCallback(() => {
    setEditInvolved(false);
    setRelatedCases(editableBaseRelatedCases);
  }, [editableBaseRelatedCases, setRelatedCases]);

  useEffect(() => {
    const caseId = caseObj?.pk ?? caseObj?.id;
    if (!caseId) {
      return;
    }
    const queryParams = deBouncedCaseSearchQuery ? { name_search: deBouncedCaseSearchQuery } : null;

    let isActive = true;

    const fetchRelatedCases = async () => {
      try {
        const relatableCases = await caseRepository.getRelatableCases(caseId, queryParams);
        if (isActive && relatableCases) {
          setCaseList(relatableCases?.results ?? relatableCases);
        }
      } catch (error) {
        if (isActive) {
          handleApiError(error);
        }
      }
    };

    fetchRelatedCases();

    return () => {
      isActive = false;
    };
  }, [caseObj, deBouncedCaseSearchQuery, caseRepository, handleApiError]);

  useEffect(() => {
    setRelatedCases(editableBaseRelatedCases);
  }, [editableBaseRelatedCases, setRelatedCases]);

  useDebouncedEffect(() => setDeBouncedCaseSearchQuery(caseSearchQuery), [caseSearchQuery], 500);

  const handleNavToRelated = async (clientId) => {
    setNavigateToRelatedCase(true);
    try {
      const navCase = await caseRepository.query({
        client: clientId,
        "data-set": "search",
      });
      const foundCasePk = navCase?.[0]?.pk ?? navCase?.[0]?.id;
      setNavigateToRelatedCase(false);
      if (foundCasePk) {
        historyNavigate("client-detail", foundCasePk);
        handleApiSuccess(navCase, "Companions");
      } else {
        throw new Error("Related case not found.");
      }
    } catch (error) {
      setNavigateToRelatedCase(false);
      handleApiError(error);
    }
  };

  const { editable: selectableCaseList } = useMemo(
    () => splitCaseCollection(Array.isArray(caseList) ? caseList : []),
    [caseList]
  );
  const autocompleteOptions = useMemo(
    () => dedupeByCaseKey([...selectableCaseList, ...relatedCases]),
    [selectableCaseList, relatedCases]
  );
  const hasRelatedCasesChanged = useMemo(
    () => !areCaseListsEqual(relatedCases, editableBaseRelatedCases),
    [editableBaseRelatedCases, relatedCases]
  );

  const renderCaseTags = useCallback(
    (value, getTagProps) =>
      value.map((option, index) => {
        const { key: tagKey, onDelete, ...tagProps } = getTagProps({ index });
        const canDelete = option?.show_delete === true;
        if (!canDelete) {
          return null;
        }
        return (
          <Chip
            key={getOptionKey(option) ?? tagKey}
            variant="outlined"
            label={getOptionLabel(option)}
            onDelete={canDelete ? onDelete : undefined}
            {...tagProps}
          />
        );
      }),
    []
  );

  const handleAutocompleteChange = useCallback(
    (value, reason) => {
      const normalizedValue = splitCaseCollection(value ?? []).editable;

      if (reason === "clear") {
        setRelatedCases([]);
        return;
      }

      const normalizedMap = new Map(normalizedValue.map((item) => [getOptionKey(item), item]));
      const nextList = [];
      const usedKeys = new Set();

      (relatedCases || []).forEach((existing) => {
        const key = getOptionKey(existing);
        if (!key || usedKeys.has(key)) {
          return;
        }

        if (normalizedMap.has(key)) {
          nextList.push(normalizedMap.get(key));
          normalizedMap.delete(key);
          usedKeys.add(key);
        } else if (existing?.show_delete === false) {
          nextList.push(existing);
          usedKeys.add(key);
        }
      });

      normalizedMap.forEach((item, key) => {
        if (!usedKeys.has(key)) {
          nextList.push(item);
          usedKeys.add(key);
        }
      });

      setRelatedCases(nextList);
    },
    [relatedCases, setRelatedCases]
  );

  return (
    <div>
      <LoadingIndicator isLoading={navigateToRelatedCase} />
      {!viewPermissions ? (
        <h2 className="my-2 text-center text-gray-500 underline">
          Content details not available for this account
        </h2>
      ) : (
        <>
          {editInvolved === false ? (
            <div className="">
              <div className="flex w-full items-center justify-between">
                <h2 className="font-semibold">Companions:</h2>
                <Tooltip title={"Update Companions"}>
                  <span>
                    <IconButton onClick={() => setEditInvolved(true)} disabled={!editPermissions}>
                      <Add />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
              <div>
                {combinedViewCases?.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {combinedViewCases.map((inv) => (
                      <Chip
                        sx={{ backgroundColor: "#deeffd" }}
                        className="flex justify-start text-sm"
                        key={getOptionKey(inv)}
                        onClick={() => handleNavToRelated(inv?.client_id)}
                        variant="filled"
                        label={truncate(getOptionLabel(inv), 40)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="relative flex items-center">
              <Autocomplete
                size="small"
                multiple
                options={autocompleteOptions}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={(opt, val) => getOptionKey(opt) === getOptionKey(val)}
                value={relatedCases}
                renderTags={renderCaseTags}
                onChange={(e, value, reason) => handleAutocompleteChange(value, reason)}
                sx={{ width: 300 }}
                renderOption={(props, option) => {
                  const { key: optionKey, ...restProps } = props;
                  return (
                    <li key={getOptionKey(option) ?? optionKey} {...restProps}>
                      {getOptionLabel(option)}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Cases"
                    onChange={(event) => setCaseSearchQuery(event.target.value)}
                  />
                )}
              />
              {loading ? (
                <div className="flex items-center px-2">
                  <p>Loading...</p>
                  <div className="relative mt-2 block pl-4">
                    <CircularProgress color="secondary" sx={{ maxWidth: 25, maxHeight: 25 }} />
                  </div>
                </div>
              ) : (
                <div className="flex items-center">
                  {hasRelatedCasesChanged && (
                    <Tooltip title="Submit">
                      <IconButton onClick={addCaseRelationship}>
                        <Check className="text-green-600" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Cancel edit">
                    <IconButton onClick={handleCancelEdit}>
                      <Close className="text-red-600" />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </div>
          )}
          <ResponseDisplay
            responseBreakdown={responseBreakdown}
            responseSuccess={responseSuccess}
          />
        </>
      )}
    </div>
  );
}
